import { useEffect, useLayoutEffect, useRef } from 'react';

interface UseEventListerOptions {
  skip?: boolean;
  options?: AddEventListenerOptions;
}

function useEventListener<K extends keyof WindowEventMap>(
  element: Window | null | undefined,
  eventName: K,
  handler: (event: WindowEventMap[K]) => void,
  opts?: UseEventListerOptions,
): void;
function useEventListener<K extends keyof HTMLElementEventMap>(
  element: HTMLElement | null | undefined,
  eventName: K,
  handler: (event: HTMLElementEventMap[K]) => void,
  opts?: UseEventListerOptions,
): void;
function useEventListener(
  element: Window | HTMLElement | null | undefined,
  eventName: keyof WindowEventMap & HTMLElementEventMap,
  handler: (ev: Event) => void,
  {
    skip = false,
    options: { capture, once, passive, signal } = {},
  }: UseEventListerOptions = {},
) {
  const handlerRef = useRef<typeof handler>(handler);

  useLayoutEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!element || skip) {
      return undefined;
    }

    const listener: typeof handler = (event) =>
      handlerRef.current.call(element, event);
    const options = { capture, once, passive, signal };

    element.addEventListener(eventName, listener, options);
    return () => {
      element.removeEventListener(eventName, listener, { capture });
    };
  }, [eventName, element, skip, capture, once, passive, signal]);
}

export default useEventListener;
